import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ScenarioList from '../../../components/ScenarioManagement/ScenarioList';
import GlobalInvestmentManager from '../../../components/InvestmentManagement/GlobalInvestmentManager';
import GlobalIncidentManager from '../../../components/IncidentManagement/GlobalIncidentManager';
import ImportExportPanel from '../../../components/ScenarioManagement/ImportExportPanel';
import { JSONPersistenceService } from '../../../services/JSONPersistenceService';
import { useToast } from '../../../hooks/useToast';
import { useTheme } from '../../../contexts/ThemeContext';
import { ScoringManagement } from '../../../components/ScoringManagement';

type TabType = 'manage' | 'investments' | 'incidents' | 'scoring';

/**
 * Scenario Management Dashboard
 * Main CRUD interface with tabbed navigation
 */
const ScenarioManagementDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('manage');
  const [refreshKey, setRefreshKey] = useState(0);
  const { showToast } = useToast();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const tabs = [
    {
      id: 'manage' as TabType,
      label: 'Manage Scenarios',
      icon: '📚',
      description: 'Browse, search, and organize scenarios'
    },
    {
      id: 'investments' as TabType,
      label: 'Investment Library',
      icon: '💰',
      description: 'Manage investment library for all scenarios'
    },
    {
      id: 'incidents' as TabType,
      label: 'Incident Library',
      icon: '🚨',
      description: 'Manage incident library for all scenarios'
    },
    {
      id: 'scoring' as TabType,
      label: 'Scoring Configuration',
      icon: '📊',
      description: 'Configure scoring rules and grade thresholds'
    }
  ];

  const handleCreateScenario = () => {
    try {
      // Navigate to scenario editor with no ID to create new scenario
      console.log('Navigating to create scenario...');
      navigate('/manage/create');
    } catch (error) {
      console.error('Error navigating to create scenario:', error);
      showToast({
        type: 'error',
        title: 'Navigation Error',
        message: 'Failed to navigate to create scenario screen'
      });
    }
  };

  const handleDeleteScenarios = async (scenarioIds: string[]) => {
    try {
      const persistence = JSONPersistenceService.getInstance();
      
      // Load current data
      const currentData = await persistence.loadData() || {
        scenarios: [],
        templates: [],
        categories: [],
        tags: [],
        dependencies: []
      };
      
      // Update scenarios - soft delete by adding deletedAt timestamp
      const scenarioMap = new Map(currentData.scenarios);
      scenarioIds.forEach(id => {
        const scenario = scenarioMap.get(id);
        if (scenario) {
          scenario.deletedAt = new Date();
          scenarioMap.set(id, scenario);
        }
      });
      
      // Save updated data
      await persistence.saveData({
        ...currentData,
        scenarios: Array.from(scenarioMap.entries())
      });
      
      showToast({
        type: 'success',
        title: 'Scenarios Deleted',
        message: `${scenarioIds.length} scenario(s) moved to trash`
      });
      
      // Trigger refresh of scenario list
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete scenarios. Please try again.'
      });
    }
  };

  const handleRestoreScenarios = async (scenarioIds: string[]) => {
    try {
      // TODO: Implement API call for restore
      showToast({
        type: 'success',
        title: 'Scenarios Restored',
        message: `${scenarioIds.length} scenario(s) restored successfully`
      });
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Restore Failed',
        message: 'Failed to restore scenarios. Please try again.'
      });
    }
  };

  const handleExportScenarios = async (scenarioIds: string[]) => {
    try {
      // TODO: Implement API call for export
      showToast({
        type: 'info',
        title: 'Export Started',
        message: 'Scenario export is being prepared...'
      });
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export scenarios. Please try again.'
      });
    }
  };



  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-navy-950 to-navy-900' : 'bg-gradient-to-br from-gray-50 to-white'}`}>
      <motion.div
        className="max-w-7xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="p-6 pb-0" variants={itemVariants}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className={`text-4xl font-light tracking-wide ${isDark ? 'bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent' : 'text-gray-900'} mb-2`}>
                Scenario Management
              </h1>
              <p className={`text-lg ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                Create, edit, and manage cybersecurity scenarios
              </p>
            </div>
            <Link
              to="/"
              className={`${isDark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors duration-200 flex items-center gap-2`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Game
            </Link>
          </div>
        </motion.div>

        {/* Tabs Navigation */}
        <motion.div 
          className="px-6 mb-6"
          variants={itemVariants}
        >
          <div className={`${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'} border rounded-2xl p-2 backdrop-blur-md`}>
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl transition-all duration-200 ${activeTab === tab.id
                      ? 'bg-professional-blue-600 text-white shadow-lg'
                      : isDark 
                        ? 'text-slate-400 hover:text-white hover:bg-white/[0.05]' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                >
                  <span className="text-xl">{tab.icon}</span>
                  <div className="text-left hidden sm:block">
                    <div className="font-medium">{tab.label}</div>
                    <div className="text-xs opacity-75">{tab.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <div className="px-6 pb-8">
          <AnimatePresence mode="wait">
            {activeTab === 'manage' && (
              <motion.div
                key="manage"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                {/* Import/Export Panel */}
                <ImportExportPanel
                  onImportComplete={(count) => {
                    showToast({
                      type: 'success',
                      title: 'Import Complete',
                      message: `Successfully imported ${count} scenario(s)`
                    });
                    setRefreshKey(prev => prev + 1);
                  }}
                  onRefresh={() => setRefreshKey(prev => prev + 1)}
                />

                {/* Scenario List */}
                <ScenarioList
                  onDelete={handleDeleteScenarios}
                  onRestore={handleRestoreScenarios}
                  onExport={handleExportScenarios}
                  onCreate={handleCreateScenario}
                  viewMode="grid"
                  refreshKey={refreshKey}
                />
              </motion.div>
            )}

            {activeTab === 'investments' && (
              <motion.div
                key="investments"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <GlobalInvestmentManager />
              </motion.div>
            )}

            {activeTab === 'incidents' && (
              <motion.div
                key="incidents"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <GlobalIncidentManager />
              </motion.div>
            )}

            {activeTab === 'scoring' && (
              <motion.div
                key="scoring"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <ScoringManagement />
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ScenarioManagementDashboard;